import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AuthShell } from "@/pages/auth/Login";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") || "");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: token.trim(), password });
      toast.success("Password reset! You can now sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset password" subtitle="Paste your reset token and choose a new password.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div className="space-y-2">
          <Label>Reset token</Label>
          <Input required value={token} onChange={(e) => setToken(e.target.value)} data-testid="reset-token-input" />
        </div>
        <div className="space-y-2">
          <Label>New password</Label>
          <div className="relative">
            <Input type={showPw ? "text" : "password"} required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="reset-password-input" />
            <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"} data-testid="reset-password-toggle"
              className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full rounded-full bg-slate-900 hover:bg-slate-800">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset password
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-slate-500">
        <Link to="/login" className="font-semibold text-sky-600 hover:underline">Back to sign in</Link>
      </p>
    </AuthShell>
  );
}
